import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

import { templateCreationRecordIdAtom } from '../template-builder/atoms';
import { type StreamState, StreamStatus } from './types';

// Key type for stream state atoms
export interface StreamStateKey {
  templateCreationRecordId: string;
  versionControlId: string;
}

// Combined stream states for both structure and example generation
// Use object keys with templateCreationRecordId and versionControlId
// This ensures both states are always initialized together
export interface CombinedStreamStates {
  structure: StreamState;
  example: StreamState;
}

// Track all keys per template for cleanup
const templateKeys = new Map<string, Set<StreamStateKey>>();

// Helper to track keys for a template
function trackKey(key: StreamStateKey) {
  if (!templateKeys.has(key.templateCreationRecordId)) {
    templateKeys.set(key.templateCreationRecordId, new Set());
  }
  templateKeys.get(key.templateCreationRecordId)?.add(key);
}

export const streamStatesAtomFamily = atomFamily(
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (_key: StreamStateKey) => {
    trackKey(_key);
    return atom<CombinedStreamStates>({
      structure: {
        status: StreamStatus.IDLE,
        abortController: new AbortController(),
        animationType: undefined,
      },
      example: {
        status: StreamStatus.IDLE,
        abortController: new AbortController(),
        animationType: undefined,
      },
    });
  },
  (a, b) =>
    a.templateCreationRecordId === b.templateCreationRecordId &&
    a.versionControlId === b.versionControlId
);

// Legacy exports for backwards compatibility - these now access the combined atom
export const structureGenerationStreamStateAtomFamily = atomFamily(
  (key: StreamStateKey) =>
    atom(
      (get) => get(streamStatesAtomFamily(key)).structure,
      (get, set, update: StreamState | ((prev: StreamState) => StreamState)) => {
        const combined = get(streamStatesAtomFamily(key));
        const newStructure =
          typeof update === 'function' ? update(combined.structure) : update;
        set(streamStatesAtomFamily(key), {
          ...combined,
          structure: newStructure,
        });
      }
    ),
  (a, b) =>
    a.templateCreationRecordId === b.templateCreationRecordId &&
    a.versionControlId === b.versionControlId
);

export const exampleGenerationStreamStateAtomFamily = atomFamily(
  (key: StreamStateKey) =>
    atom(
      (get) => get(streamStatesAtomFamily(key)).example,
      (get, set, update: StreamState | ((prev: StreamState) => StreamState)) => {
        const combined = get(streamStatesAtomFamily(key));
        const newExample =
          typeof update === 'function' ? update(combined.example) : update;
        set(streamStatesAtomFamily(key), {
          ...combined,
          example: newExample,
        });
      }
    ),
  (a, b) =>
    a.templateCreationRecordId === b.templateCreationRecordId &&
    a.versionControlId === b.versionControlId
);

// Track previous template creation record ID for cleanup
let previousStreamTemplateId: string | undefined;

// Effect atom that cleans up stream state atom family caches when templateCreationRecordId changes
export const cleanupStreamStateAtomFamiliesAtom = atom(
  (get) => get(templateCreationRecordIdAtom),
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  (get, _set, _update?: unknown) => {
    const currentId = get(templateCreationRecordIdAtom);

    // If the ID has changed and we have a previous ID, clean up the old atom family entries
    if (previousStreamTemplateId && previousStreamTemplateId !== currentId) {
      const keysToRemove = templateKeys.get(previousStreamTemplateId);

      if (keysToRemove) {
        // Remove all atom family entries for the old template
        keysToRemove.forEach((key) => {
          streamStatesAtomFamily.remove(key);
          structureGenerationStreamStateAtomFamily.remove(key);
          exampleGenerationStreamStateAtomFamily.remove(key);
        });

        // Clean up the tracking map
        templateKeys.delete(previousStreamTemplateId);
      }
    }

    // Update the tracked previous ID
    previousStreamTemplateId = currentId;
  }
);
